// Shared helpers used across the warehouse module UI. Pulled out of the
// component files during the simplify pass so three copies of the same
// type-color table and the same `setTimeout` banner dance don't drift.
//
// Only put things here that are genuinely cross-component. Anything used in
// a single file stays in that file.

import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Item type constants
// ---------------------------------------------------------------------------

// Keep in sync with the StorageItem.type enum on the backend.
export const ITEM_TYPES = ['drum', 'europallet', 'coil'];

export function typeBadgeColor(type) {
  switch (type) {
    case 'drum':
      return 'warning';
    case 'europallet':
      return 'info';
    case 'coil':
      return 'secondary';
    default:
      return 'light';
  }
}

// ---------------------------------------------------------------------------
// Trip status constants
// ---------------------------------------------------------------------------

// Keep in sync with the ShipmentTrip.status enum on the backend.
export const TRIP_STATUS_COLORS = {
  draft: 'secondary',
  loading: 'warning',
  loaded: 'info',
  shipped: 'success',
  invoiced: 'dark',
};

export function tripStatusColor(status) {
  return TRIP_STATUS_COLORS[status] || 'secondary';
}

// ---------------------------------------------------------------------------
// Physical footprint — used by the 3D truck loader
// ---------------------------------------------------------------------------
//
// Each return value is:
//   { shape: 'box' | 'cylinder',
//     lengthM, widthM, heightM,          // bounding box on the truck bed
//     diameterM?, barrelLengthM? }       // extras for cylinders (drums)
//
// Coordinate convention: lengthM runs along the truck's long axis (X),
// widthM across the truck (Z), heightM is vertical (Y).
//
// DRUMS always rest on their flange (flange flat on the floor, cylinder
// axis vertical) — this is how every Romanian cable factory actually loads
// them, because a drum on its side rolls. That makes:
//   lengthM = flangeDiameterM   (square bounding box on the bed)
//   widthM  = flangeDiameterM
//   heightM = barrelLengthM     (distance between the two flanges)
//
// Europallets are boxes sized at the EUR standard (1.2 × 0.8 × ~1.5 tall),
// loaded with the long side along the truck length — 3 fit across a 2.48m
// tautliner, 11 rows × 3 = 33 pallets.
//
// Coils are small boxes — 0.8 × 0.8 × 0.6 — a rough guess good enough for v1.
//
// If item.physical has explicit fields, those win over the type defaults.
const TYPE_FALLBACKS = {
  europallet: { shape: 'box', lengthM: 1.2, widthM: 0.8, heightM: 1.5 },
  coil: { shape: 'box', lengthM: 0.8, widthM: 0.8, heightM: 0.6 },
  drum: { shape: 'cylinder', diameterM: 1.2, barrelLengthM: 0.8 },
};

// Orientation values:
//   drum:       'upright'  (flange on the floor, axis vertical, default)
//               'side-x'   (axis along truck length +X)
//               'side-z'   (axis across truck width +Z)
//   europallet: 'long-x'   (1.2m along truck length, default)
//               'long-z'   (1.2m across truck width)
//
// Callers that don't care about rotation can pass no second argument.
export function itemFootprint(item, orientation) {
  if (!item) return null;
  const fallback = TYPE_FALLBACKS[item.type] || TYPE_FALLBACKS.europallet;
  const phys = item.physical || {};

  if (fallback.shape === 'cylinder') {
    const diameterM = phys.flangeDiameterM || fallback.diameterM;
    const barrelLengthM = phys.barrelLengthM || fallback.barrelLengthM;
    const o = orientation || 'upright';
    if (o === 'side-x') {
      return {
        shape: 'cylinder', orientation: o,
        lengthM: barrelLengthM,
        widthM: diameterM,
        heightM: diameterM,
        diameterM, barrelLengthM,
      };
    }
    if (o === 'side-z') {
      return {
        shape: 'cylinder', orientation: o,
        lengthM: diameterM,
        widthM: barrelLengthM,
        heightM: diameterM,
        diameterM, barrelLengthM,
      };
    }
    return {
      shape: 'cylinder', orientation: 'upright',
      lengthM: diameterM,
      widthM: diameterM,
      heightM: barrelLengthM,
      diameterM, barrelLengthM,
    };
  }

  // Europallet (or legacy coil). Rotate 90° in-place if long-z.
  const baseLengthM = phys.widthM || fallback.lengthM;
  const baseWidthM = phys.depthM || fallback.widthM;
  const heightM = phys.heightM || fallback.heightM;
  const o = orientation || 'long-x';
  if (o === 'long-z') {
    return { shape: 'box', orientation: o, lengthM: baseWidthM, widthM: baseLengthM, heightM };
  }
  return { shape: 'box', orientation: 'long-x', lengthM: baseLengthM, widthM: baseWidthM, heightM };
}

// Axis-aligned bounding box on the truck floor for a placement
// ({ item, x, z, orientation }). Returns world-box edges in truck-local coords.
export function placementAABB(placement) {
  if (!placement) return null;
  const fp = itemFootprint(placement.item, placement.orientation);
  if (!fp) return null;
  const x = Number(placement.x) || 0;
  const z = Number(placement.z) || 0;
  return {
    xMin: x - fp.lengthM / 2,
    xMax: x + fp.lengthM / 2,
    zMin: z - fp.widthM / 2,
    zMax: z + fp.widthM / 2,
    lengthM: fp.lengthM,
    widthM: fp.widthM,
  };
}

// Strict AABB overlap — touching edges don't count as collision.
// Small epsilon to avoid flagging items that share a border (grid-packed layouts).
const OVERLAP_EPS = 1e-3;
export function aabbOverlaps(a, b) {
  return a.xMin + OVERLAP_EPS < b.xMax &&
         b.xMin + OVERLAP_EPS < a.xMax &&
         a.zMin + OVERLAP_EPS < b.zMax &&
         b.zMin + OVERLAP_EPS < a.zMax;
}

// Returns a Set of placement ids that collide with at least one other
// placement. Items on different Y levels (stacked) don't collide with each
// other — they sit above.
export function findPlacementCollisions(placements) {
  const result = new Set();
  if (!Array.isArray(placements) || placements.length < 2) return result;
  const boxes = placements.map(p => ({ id: p.id, y: p.y || 0, aabb: placementAABB(p) }));
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const a = boxes[i], b = boxes[j];
      if (!a.aabb || !b.aabb) continue;
      if (Math.abs(a.y - b.y) > 1e-3) continue; // different levels — skip
      if (aabbOverlaps(a.aabb, b.aabb)) {
        result.add(a.id);
        result.add(b.id);
      }
    }
  }
  return result;
}

// True if placement's footprint is entirely within [0, truck.lengthM] ×
// [0, truck.widthM]. Used to flag items that spill past the truck walls.
export function placementFitsTruck(placement, truck) {
  if (!placement || !truck) return false;
  const aabb = placementAABB(placement);
  if (!aabb) return false;
  return aabb.xMin >= -OVERLAP_EPS &&
         aabb.xMax <= truck.lengthM + OVERLAP_EPS &&
         aabb.zMin >= -OVERLAP_EPS &&
         aabb.zMax <= truck.widthM + OVERLAP_EPS;
}

// True if placement is *meant* to be on the truck (i.e., its center sits
// within the bed footprint). Items intentionally parked in staging get z<0.
export function placementOnTruckBed(placement, truck) {
  if (!placement || !truck) return false;
  const x = Number(placement.x) || 0;
  const z = Number(placement.z) || 0;
  return x >= 0 && x <= truck.lengthM && z >= 0 && z <= truck.widthM;
}

// Human-readable drum spec from an item's physical dims, e.g.
// "Ø1200 × 700 · wood". Returns null for non-drums / missing dims. Used to show
// which catalog drum an item sits on in the truck-loading UI.
export function drumSpecLabel(item) {
  if (!item || item.type !== 'drum') return null;
  const p = item.physical || {};
  const d = p.flangeDiameterM ? Math.round(p.flangeDiameterM * 1000) : null;
  const w = p.barrelLengthM ? Math.round(p.barrelLengthM * 1000) : null;
  if (!d && !w) return null;
  const dims = `Ø${d ?? '?'} × ${w ?? '?'}`;
  return p.drumType ? `${dims} · ${p.drumType}` : dims;
}

// Cycle orientation to the next option for a given item type.
// Drums: upright → side-x → side-z → upright
// Europallets: long-x → long-z → long-x
export function nextOrientation(item, current) {
  const type = item?.type;
  if (type === 'drum') {
    const order = ['upright', 'side-x', 'side-z'];
    const idx = Math.max(0, order.indexOf(current || 'upright'));
    return order[(idx + 1) % order.length];
  }
  return current === 'long-z' ? 'long-x' : 'long-z';
}

// ---------------------------------------------------------------------------
// Movement action labels + colors
// ---------------------------------------------------------------------------

// Keep in sync with the WarehouseMovement.action enum on the backend.
export const MOVEMENT_ACTION_LABELS = {
  produced: 'Produced',
  put_away: 'Put away',
  moved: 'Moved',
  reserved: 'Reserved',
  unreserved: 'Unreserved',
  loaded: 'Loaded',
  unloaded: 'Unloaded',
  shipped: 'Shipped',
  scrapped: 'Scrapped',
};

export const MOVEMENT_ACTION_COLORS = {
  produced: 'secondary',
  put_away: 'success',
  moved: 'info',
  reserved: 'primary',
  unreserved: 'secondary',
  loaded: 'warning',
  unloaded: 'secondary',
  shipped: 'dark',
  scrapped: 'danger',
};

export function movementActionLabel(action) {
  return MOVEMENT_ACTION_LABELS[action] || action;
}

export function movementActionColor(action) {
  return MOVEMENT_ACTION_COLORS[action] || 'secondary';
}

// ---------------------------------------------------------------------------
// Error message extraction
// ---------------------------------------------------------------------------

// Axios errors put the server's message under response.data.message; fall
// back to the JS Error message, then a caller-supplied default.
export function extractErrorMessage(err, fallback = 'Something went wrong') {
  return err?.response?.data?.message || err?.message || fallback;
}

// ---------------------------------------------------------------------------
// Tiny client-side filter helpers (used by UnplacedItemsPanel + LoadableItemsPanel)
// ---------------------------------------------------------------------------

export function matchesTypeFilter(item, filterType) {
  return filterType === 'all' || item.type === filterType;
}

export function matchesTextSearch(item, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return `${item.serial} ${item.cableName}`.toLowerCase().includes(needle);
}

export function bySerialAsc(a, b) {
  return a.serial > b.serial ? 1 : -1;
}

// ---------------------------------------------------------------------------
// Flash banner hook
// ---------------------------------------------------------------------------
//
// Three components (drawer, put-away panel, trip detail page) all want the
// same pattern: set `{variant, text}` on success/error, auto-hide success
// after a few seconds, keep errors sticky. The inline implementations all
// leaked their `setTimeout` when the component unmounted mid-flash, which
// triggers "setState on unmounted component" warnings. This hook owns the
// timer via a ref and clears it on unmount and on re-flash.
export function useFlashBanner(defaultAutoHideMs = 3000) {
  const [banner, setBanner] = useState(null);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const flash = useCallback(
    (variant, text, { autoHide = true, ms = defaultAutoHideMs } = {}) => {
      clearTimer();
      setBanner({ variant, text });
      if (autoHide) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setBanner(null);
        }, ms);
      }
    },
    [clearTimer, defaultAutoHideMs]
  );

  const clear = useCallback(() => {
    clearTimer();
    setBanner(null);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  return { banner, flash, clear };
}
